// RequestNoteChip.tsx — CTSK-TBD (child of CTSK-166).
//
// Pill-shaped chip for the External Clients → "Onboarding a new client"
// card header. Hover / focus shows a tooltip with the first N lines of
// the request-note prompt plus a one-sentence usage note; click (or
// Enter / Space) copies the FULL prompt to the clipboard via the shared
// copyToClipboard helper and flashes "Copied" for ~1.5s.
//
// Traps honoured (brief §8):
//   - copyToClipboard is called synchronously inside the gesture handler
//     (no setTimeout wrapper) so the execCommand fallback keeps its user
//     gesture on http://192.168.* dev origins; UI state updates in the
//     resolved promise.
//   - Tooltip is position:absolute inside the chip wrapper (no fixed),
//     z-index above the sticky header/menus (30–80) and well below any
//     modal (1000+).
//   - Preview is plain text in a <pre>; never dangerouslySetInnerHTML.
//   - Tab is not swallowed: the tooltip holds no focusable children
//     except the MCP-setup shortcut link, and focus leaves naturally.
//   - All colours via CSS variables (dark-mode safe).

import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Check, ClipboardList, CircleAlert, ExternalLink } from 'lucide-react';
import { copyToClipboard } from '../../lib/clipboard.js';
import { REQUEST_NOTE_PREVIEW_LINES, REQUEST_NOTE_USAGE, previewOf } from './request-note-text.js';

type CopyState = 'idle' | 'copied' | 'failed';

const COPIED_HOLD_MS = 1500;
const FAILED_HOLD_MS = 2500;

export function RequestNoteChip(props: {
  prompt: string;
  previewLines?: number;
  /** Router hook for the "tasko-mcp setup" shortcut inside the preview. */
  onNavigate: (path: string) => void;
}): JSX.Element {
  const { prompt, previewLines = REQUEST_NOTE_PREVIEW_LINES, onNavigate } = props;
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const [hovered, setHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipId = useId();

  useEffect(() => {
    return () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    };
  }, []);

  function settle(next: Exclude<CopyState, 'idle'>) {
    setCopyState(next);
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(
      () => setCopyState('idle'),
      next === 'copied' ? COPIED_HOLD_MS : FAILED_HOLD_MS,
    );
  }

  /** Must run synchronously from the gesture — see header comment. Never throws. */
  function doCopy() {
    let p: Promise<boolean>;
    try {
      p = copyToClipboard(prompt);
    } catch {
      settle('failed');
      return;
    }
    p.then(
      (ok) => settle(ok ? 'copied' : 'failed'),
      () => settle('failed'),
    );
  }

  function onKeyDown(e: KeyboardEvent<HTMLSpanElement>) {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      doCopy();
    }
  }

  const open = hovered || focusWithin;
  const label = copyState === 'copied' ? 'Copied' : copyState === 'failed' ? 'Copy failed' : 'Request note';
  const Icon = copyState === 'copied' ? Check : copyState === 'failed' ? CircleAlert : ClipboardList;
  const chipColor =
    copyState === 'copied'
      ? 'var(--success-primary)'
      : copyState === 'failed'
        ? 'var(--danger-primary)'
        : 'var(--accent-primary)';

  return (
    <span
      className="request-note-chip"
      style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocusWithin(true)}
      onBlur={(e) => {
        // Hide only when focus leaves the wrapper entirely (focus-within).
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocusWithin(false);
      }}
    >
      <span
        role="button"
        tabIndex={0}
        aria-label="Request note — click to copy"
        aria-describedby={tooltipId}
        data-copy-state={copyState}
        onClick={doCopy}
        onKeyDown={onKeyDown}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '3px 10px 3px 8px',
          borderRadius: 999,
          border: `1px solid color-mix(in srgb, ${chipColor} 45%, var(--border-subtle))`,
          background: `color-mix(in srgb, ${chipColor} 10%, var(--surface-raised))`,
          color: chipColor,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.4,
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        <Icon size={13} aria-hidden />
        <span>{label}</span>
      </span>

      <div
        id={tooltipId}
        role="tooltip"
        aria-hidden={!open}
        style={{
          display: open ? 'block' : 'none',
          position: 'absolute',
          top: 'calc(100% + 6px)',
          right: 0,
          zIndex: 100,
          width: 'min(520px, 80vw)',
          padding: 10,
          background: 'var(--surface-raised)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-strong)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-md)',
          fontSize: 12,
          lineHeight: 1.5,
          textAlign: 'left',
          whiteSpace: 'normal',
        }}
      >
        <pre
          style={{
            margin: 0,
            padding: 8,
            background: 'var(--surface-sunken)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-sm)',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            lineHeight: 1.45,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            color: 'var(--text-secondary)',
          }}
        >
          {previewOf(prompt, previewLines)}
        </pre>
        <div style={{ marginTop: 8, color: 'var(--text-secondary)' }}>
          {REQUEST_NOTE_USAGE}{' '}
          <a
            href="/help/tasko-mcp-setup"
            onClick={(e) => { e.preventDefault(); onNavigate('/help/tasko-mcp-setup'); }}
            style={{ color: 'var(--accent-primary)', textDecoration: 'none', whiteSpace: 'nowrap' }}
          >
            MCP setup <ExternalLink size={11} aria-hidden style={{ verticalAlign: '-1px' }} />
          </a>
        </div>
      </div>
    </span>
  );
}
